<script setup lang="ts">
import AppButton from '@/components/ui/AppButton.vue'
import Counter from '@/components/ui/Counter.vue'
import Loading from '@/components/ui/Loading.vue'
import { ref, onMounted } from 'vue'
import { loadStripe } from '@stripe/stripe-js'
import { createRemoteDiagnose } from '@/services/remote-diagnose'
import { useRemoteDiagnose } from '@/stores/remote-diagnose'
import { pinia } from '@/stores'
import { storeToRefs } from 'pinia'
import { getLimitDate, getTimeLeft, removeLimitDate, setLimitDate } from '@/utils/limit-date'
import { useRouterStore } from '@/stores/router'
import { setStore } from '@/utils/remote-diagnose'
import { cleanQueryString } from '@/utils/router'
import { useLang } from '@/composables/use-lang'
import PaymentContent from '@/lang/payment.json'

const props = defineProps({
  aws_url: {
    type: String,
    required: true
  },
  stripe_key: {
    type: String,
    default: ''
  }
})

const { push, resetParams } = useRouterStore(pinia)
const remoteDiagnoseStore = useRemoteDiagnose(pinia)

const { state } = storeToRefs(remoteDiagnoseStore)

const loading = ref<boolean>(true)
const loadingPay = ref<boolean>(false)
const error = ref<boolean>(false)
const stripeError = ref({
  status: false,
  message: ''
})
const stripePromise = loadStripe(props.stripe_key)
const stripe = ref<any>(null)
const elements = ref<any>(null)

const emit = defineEmits(['scroll'])

const langContent = useLang(PaymentContent)

const handleSubmit = async () => {
  if (stripe.value) {
    stripeError.value.status = false
    stripeError.value.message = ''
    loadingPay.value = true
    const { error } = await stripe.value.confirmPayment({
      elements: elements.value,
      confirmParams: {
        return_url: window.location.href
      }
    })
    if (error.type === 'card_error' || error.type === 'validation_error') {
      console.log('Displaying error message', error.message)
      stripeError.value.status = true
      stripeError.value.message = error.message
    } else {
      stripeError.value.status = true
      stripeError.value.message = error
      console.log('Handle any other unexpected error here', error)
    }
    loadingPay.value = false
  }
}

const handleError = () => {
  push('address')
}

const mountStripeElements = async (result: any) => {
  stripe.value = (await stripePromise) as any
  const appearance = { theme: 'flat', arguments: { labels: 'floating' } }
  elements.value = stripe.value.elements({
    appearance,
    clientSecret: result.payment_client_secret
  })
  const paymentElement = elements.value.create('payment')
  paymentElement.mount('#payment-element')
}

onMounted(async () => {
  emit('scroll')
  cleanQueryString()

  // validate if the time already expired and redirect it to timesup page
  const limitDate = getLimitDate()
  if (limitDate) {
    const timeLeft = getTimeLeft(limitDate)
    if (timeLeft <= 0) {
      removeLimitDate()
      push('timesup')
      return null
    }
  }

  if (state.value.phone_number) {
    resetParams()
    const result = await createRemoteDiagnose(state.value, props.aws_url)

    //  set error if create payment failed
    if (!result.payment_client_secret) {
      error.value = true
      loading.value = false
      return null
    }

    //set current state in local storage
    setStore(state.value)
    loading.value = false

    //set limit date for counter
    setLimitDate(result.created_at)

    mountStripeElements(result)
  } else {
    loading.value = false
    removeLimitDate()
    push('home')
  }
})
</script>

<template>
  <div v-if="langContent">
    <div v-if="error">
      <p class="text-sweet-brown">{{ langContent.error_message }}</p>
      <app-button @click="handleError">{{ langContent.error_button }}</app-button>
    </div>
    <Loading v-if="loading && !error" />
    <form @submit.prevent="handleSubmit">
      <counter v-if="!loading" />
      <slot></slot>
      <p v-if="stripeError.status" class="text-sweet-brown">{{ stripeError.message }}</p>
      <app-button
        v-if="!loading"
        id="submit"
        type="submit"
        class="mt-10"
        :loading="loadingPay"
        :disabled="loadingPay"
        >Pay Now</app-button
      >
    </form>
  </div>
</template>
